import React, { useState } from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import { format } from 'date-fns'
import EditIcon from '@mui/icons-material/Edit'
import DownloadIcon from '@mui/icons-material/Download'

import axios from '../../dataClient/dataClient'
import { handleDownloadResponse } from '../../util/downloadFile'
import { FlexColumn, FlexRow } from '../styles/Containers'
import { ContentRow } from '../styles/TableStyles'
import { BoldMedBodyText, MedBodyText, ErrorText } from '../styles/Text'
import { SecondaryIconButton } from '../common/Button'
import AssignedUserChip from '../common/AssignedUserChip'

const UserContainer = styled(FlexRow)`
  flex-basis: 12%;
  min-width: 75px;
  align-items: center;
`

const NameContainer = styled.div`
  flex-basis: ${props => (props.$large ? '60%' : '54%')};
`

const DateTimeContainer = styled(FlexRow)`
  flex-basis: ${props => (props.$large ? '40%' : '34%')};
  justify-content: flex-end;
  gap: 5px;
  flex-wrap: wrap;
  word-break: break-word;
`

const ActionContainer = styled(FlexColumn)`
  width: ${props => (props.$large ? '63px' : '29px')};
  flex-shrink: 0;
  align-items: flex-end;
  gap: 0;
`

const MiniErrorText = styled(ErrorText)`
  font-size: 10px;
  text-align: right;
`

function CommunicationRow({ communication, disableEditing = false, onEditCommunication = () => {}, handleSelectCommunication = () => {} }) {
  const [downloadLoading, setDownloadLoading] = useState(false)
  const [downloadError, setDownloadError] = useState(false)

  function handleSelect() {
    handleSelectCommunication(communication.id)
  }

  function handleRowKeyDown(event) {
    if (event.target !== event.currentTarget) return

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      handleSelect()
    }
  }

  function handleEditClick(event) {
    event.stopPropagation()
    if (communication.state === 'DRAFT') onEditCommunication(communication.id)
  }

  function handleDownloadClick(event) {
    event.stopPropagation()

    setDownloadError(false)
    setDownloadLoading(true)
    const fileName = `${communication.name}.pdf`

    axios
      .get(`/communication/${communication.id}/download`, { responseType: 'blob' })
      .then(response => handleDownloadResponse(response, fileName, 'download'))
      .catch(e => {
        setDownloadError(true)
        console.error('an error occurred while downloading the communication', communication.name, e)
      })
      .finally(() => {
        setDownloadLoading(false)
      })
  }

  const isDraft = communication.state === 'DRAFT'
  const date = isDraft ? communication.updatedAt : communication.sentAt

  return (
    <ContentRow role="button" tabIndex={0} onClick={handleSelect} onKeyDown={handleRowKeyDown}>
      {!isDraft && (
        <UserContainer>
          {communication.sentBy && (
            <AssignedUserChip
              firstName={communication.sentBy.firstName}
              lastName={communication.sentBy.lastName}
              color={communication.sentBy.chipColor}
              isShort
              labelText="Sent by"
            />
          )}
        </UserContainer>
      )}
      <NameContainer $large={isDraft}>
        <BoldMedBodyText>{communication.name}</BoldMedBodyText>
      </NameContainer>
      <DateTimeContainer $large={isDraft}>
        <BoldMedBodyText>{date ? format(date, 'MMM-dd-yyyy') : '—'}</BoldMedBodyText>
        {date && <MedBodyText>{format(date, 'hh:mm a')}</MedBodyText>}
      </DateTimeContainer>
      <ActionContainer $large={isDraft}>
        <FlexRow gap="5px">
          {communication.state === 'DRAFT' && (
            <SecondaryIconButton onClick={handleEditClick} aria-label={`Edit communication ${communication.name}`} disabled={disableEditing}>
              <EditIcon />
            </SecondaryIconButton>
          )}
          <SecondaryIconButton
            loading={downloadLoading}
            onClick={handleDownloadClick}
            aria-label={`Download communication ${communication.name} as PDF`}>
            <DownloadIcon />
          </SecondaryIconButton>
        </FlexRow>
        {downloadError && <MiniErrorText role="alert">Error, try again.</MiniErrorText>}
      </ActionContainer>
    </ContentRow>
  )
}

CommunicationRow.propTypes = {
  communication: PropTypes.shape({
    id: PropTypes.string.isRequired,
    state: PropTypes.oneOf(['DRAFT', 'SENT']).isRequired,
    name: PropTypes.string.isRequired,
    sentAt: PropTypes.instanceOf(Date),
    updatedAt: PropTypes.instanceOf(Date),
    sentBy: PropTypes.shape({
      firstName: PropTypes.string.isRequired,
      lastName: PropTypes.string.isRequired,
      chipColor: PropTypes.string.isRequired
    })
  }).isRequired,
  disableEditing: PropTypes.bool,
  onEditCommunication: PropTypes.func,
  handleSelectCommunication: PropTypes.func
}

export default CommunicationRow
